
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Home, Building2, Briefcase, Wrench, BarChart3, Bot, Settings, LogOut, Plus, Calculator, ArrowRight } from 'lucide-react';
import { PageView } from '../../types';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  setPage: (page: PageView) => void;
  onLogout: () => void;
  actions: {
    addProperty: () => void;
    addTask: () => void;
  };
}

interface CommandItem {
  id: string;
  label: string;
  category: string;
  icon: React.ElementType;
  action: () => void;
  shortcut?: string;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, setPage, onLogout, actions }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // NOTE: In Emergent workspace (CRA+TS), stale TS errors may sometimes appear in supervisor logs.
  // Ensure we don't reference non-existent page routes.
  const commands: CommandItem[] = [
    // Navigation
    { id: 'nav-dash', label: 'Go to Dashboard', category: 'Navigation', icon: Home, action: () => setPage('dashboard') },
    { id: 'nav-props', label: 'Go to Properties', category: 'Navigation', icon: Building2, action: () => setPage('properties'), shortcut: 'G P' },
    { id: 'nav-deals', label: 'Go to Deals', category: 'Navigation', icon: Briefcase, action: () => setPage('deals') },
    { id: 'nav-main', label: 'Go to Operations', category: 'Navigation', icon: Wrench, action: () => setPage('maintenance') },
    { id: 'nav-anl', label: 'Go to Analytics', category: 'Navigation', icon: BarChart3, action: () => setPage('analytics') },
    { id: 'nav-ai', label: 'Ask AI Advisor', category: 'Navigation', icon: Bot, action: () => setPage('dashboard') },
    
    // Actions
    { id: 'act-add-prop', label: 'Add New Asset', category: 'Actions', icon: Plus, action: actions.addProperty, shortcut: 'C A' },
    { id: 'act-add-task', label: 'Create Maintenance Task', category: 'Actions', icon: Wrench, action: actions.addTask },
    { id: 'act-calc', label: 'Open Tax Calculator', category: 'Actions', icon: Calculator, action: () => setPage('dashboard') },
    
    // System
    { id: 'sys-logout', label: 'Log Out', category: 'System', icon: LogOut, action: onLogout },
  ];

  const filteredCommands = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(query.toLowerCase()) || 
    cmd.category.toLowerCase().includes(query.toLowerCase())
  );

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Key handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
          onClose();
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" 
            onClick={onClose}
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden relative z-10 flex flex-col max-h-[60vh]"
          >
            {/* Search Input */}
            <div className="flex items-center px-4 py-4 border-b border-slate-800">
              <Search className="w-5 h-5 text-slate-400 mr-3" />
              <input 
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a command or search..."
                className="bg-transparent border-none outline-none text-white placeholder-slate-500 w-full text-lg font-medium"
              />
              <div className="hidden md:flex gap-2">
                 <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700">ESC</span>
              </div>
            </div>

            {/* Results List */}
            <div className="overflow-y-auto p-2 custom-scrollbar">
              {filteredCommands.length > 0 ? (
                filteredCommands.map((cmd, idx) => (
                  <div 
                    key={cmd.id}
                    onClick={() => { cmd.action(); onClose(); }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition-all ${
                      idx === selectedIndex 
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20' 
                      : 'text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <cmd.icon className={`w-5 h-5 ${idx === selectedIndex ? 'text-white' : 'text-slate-500'}`} />
                      <span className="font-medium">{cmd.label}</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {cmd.shortcut && idx !== selectedIndex && (
                            <span className="text-[10px] font-mono opacity-50 border border-slate-700 rounded px-1">{cmd.shortcut}</span>
                        )}
                        {idx === selectedIndex && (
                            <ArrowRight className="w-4 h-4 opacity-80" />
                        )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-slate-500">
                  <p>No results found.</p>
                </div>
              )}
            </div>
            
            <div className="bg-slate-950 border-t border-slate-800 px-4 py-2 flex justify-between items-center text-[10px] text-slate-500">
                <span>DC PropChain OS v2.5</span>
                <div className="flex gap-2">
                    <span>↑↓ to navigate</span>
                    <span>↵ to select</span>
                </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
